/**
 * Document Attributes Plugin
 *
 * This plugin stores document-level attributes (selectOptions, runConfiguration)
 * in ProseMirror plugin state instead of doc.attrs, preventing NodeView recreation
 * when these attributes change.
 *
 * Features:
 * - Stores selectOptions and runConfiguration in plugin state
 * - Bidirectional sync with Y.js for collaboration
 * - Transparent migration from legacy doc.attrs
 * - Zero NodeView recreations on attribute updates
 */

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import type { Transaction, EditorState } from "@tiptap/pm/state";
import * as Y from "yjs";
import { isEqual } from "lodash";

import {
  type DocumentAttributesState,
  type DocumentAttributesAction,
  type SelectOptionsState,
  DEFAULT_DOCUMENT_ATTRIBUTES,
} from "./documentAttributes.types";
import type { RunConfiguration } from "@/automation/run/RunConfigurationExtension";

// ============================================================================
// PLUGIN KEY
// ============================================================================

export const docAttrPluginKey = new PluginKey<DocumentAttributesState>(
  "documentAttributes"
);

// ============================================================================
// PLUGIN OPTIONS
// ============================================================================

export interface DocumentAttributesPluginOptions {
  /**
   * Y.Doc instance for collaborative sync
   * If provided, plugin will sync state with Y.Map("documentAttributes")
   */
  document: Y.Doc | null;

  /**
   * Y.Map field name for storing attributes
   * @default "documentAttributes"
   */
  field?: string;
}

// ============================================================================
// PLUGIN STATE REDUCER
// ============================================================================

function applyAction(
  currentState: DocumentAttributesState,
  action: DocumentAttributesAction
): DocumentAttributesState {
  switch (action.type) {
    case "SET_SELECT_OPTIONS":
      return {
        ...currentState,
        selectOptions: action.payload,
      };

    case "SET_LABWARE":
      return {
        ...currentState,
        selectOptions: {
          ...currentState.selectOptions,
          labware: action.payload,
        },
      };

    case "UPDATE_LABWARE":
      return {
        ...currentState,
        selectOptions: {
          ...currentState.selectOptions,
          labware: currentState.selectOptions.labware.map((lw) =>
            lw.id === action.payload.id
              ? { ...lw, ...action.payload.updates }
              : lw
          ),
        },
      };

    case "DELETE_LABWARE":
      return {
        ...currentState,
        selectOptions: {
          ...currentState.selectOptions,
          labware: currentState.selectOptions.labware.filter(
            (lw) => lw.id !== action.payload.id
          ),
        },
      };

    case "SET_RUN_CONFIGURATION":
      return {
        ...currentState,
        runConfiguration: action.payload,
      };

    case "INIT_FROM_SOURCE":
      return {
        ...currentState,
        ...action.payload,
      };

    default:
      return currentState;
  }
}

// ============================================================================
// INITIAL STATE BUILDER
// ============================================================================

function buildInitialState(
  editorState: EditorState,
  yDoc: Y.Doc | null,
  field: string
): DocumentAttributesState {
  // Priority: Y.Map > doc.attrs > defaults

  let selectOptions: SelectOptionsState =
    DEFAULT_DOCUMENT_ATTRIBUTES.selectOptions;
  let runConfiguration: RunConfiguration | null =
    DEFAULT_DOCUMENT_ATTRIBUTES.runConfiguration;

  // Check Y.Map first (most authoritative for collaborative docs)
  if (yDoc) {
    const yMap = yDoc.getMap(field);
    const ySelectOptions = yMap.get("selectOptions") as
      | SelectOptionsState
      | undefined;
    const yRunConfig = yMap.get("runConfiguration") as
      | RunConfiguration
      | undefined;

    if (ySelectOptions) {
      selectOptions = ySelectOptions;
    }
    if (yRunConfig !== undefined) {
      runConfiguration = yRunConfig;
    }
  }

  // Fallback to doc.attrs for legacy documents (if Y.Map was empty)
  const docAttrs = editorState.doc.attrs || {};

  if (
    selectOptions === DEFAULT_DOCUMENT_ATTRIBUTES.selectOptions &&
    docAttrs.selectOptions
  ) {
    selectOptions = docAttrs.selectOptions as SelectOptionsState;
  }

  if (
    runConfiguration === DEFAULT_DOCUMENT_ATTRIBUTES.runConfiguration &&
    docAttrs.runConfiguration !== undefined
  ) {
    runConfiguration = docAttrs.runConfiguration as RunConfiguration;
  }

  const initialState = { selectOptions, runConfiguration };

  console.log("[DocumentAttributesPlugin] Initialized with state:", {
    source: yDoc ? "Y.Map + doc.attrs fallback" : "doc.attrs only",
    labwareCount: selectOptions.labware?.length ?? 0,
    hasRunConfig: runConfiguration !== null,
  });

  return initialState;
}

// ============================================================================
// Y.JS SYNC VIEW HANDLER
// ============================================================================

function createYjsSyncView(
  view: EditorView,
  yDoc: Y.Doc | null,
  field: string
): { update: () => void; destroy: () => void } {
  if (!yDoc) {
    return { update: () => {}, destroy: () => {} };
  }

  const yMap = yDoc.getMap(field);
  let isUpdatingFromYjs = false;
  let previousState: DocumentAttributesState | undefined;

  // Observer for Y.js → Plugin State sync
  const yMapObserver = () => {
    if (isUpdatingFromYjs) return;
    isUpdatingFromYjs = true;

    const ySelectOptions = yMap.get("selectOptions") as
      | SelectOptionsState
      | undefined;
    const yRunConfig = yMap.get("runConfiguration") as
      | RunConfiguration
      | undefined;

    const currentState = docAttrPluginKey.getState(view.state);

    // Only dispatch if different
    const selectOptionsChanged = !isEqual(
      ySelectOptions,
      currentState?.selectOptions
    );
    const runConfigChanged = !isEqual(
      yRunConfig,
      currentState?.runConfiguration
    );

    if (selectOptionsChanged || runConfigChanged) {
      const action: DocumentAttributesAction = {
        type: "INIT_FROM_SOURCE",
        payload: {
          ...(selectOptionsChanged && ySelectOptions
            ? { selectOptions: ySelectOptions }
            : {}),
          ...(runConfigChanged ? { runConfiguration: yRunConfig ?? null } : {}),
        },
      };

      const tr = view.state.tr.setMeta(docAttrPluginKey, action);
      view.dispatch(tr);
    }

    // Use setTimeout to reset flag after the transaction cycle
    setTimeout(() => {
      isUpdatingFromYjs = false;
    }, 0);
  };

  yMap.observe(yMapObserver);

  return {
    update: () => {
      const currentState = docAttrPluginKey.getState(view.state);

      if (isUpdatingFromYjs) {
        // Still track state so we don't detect a false "change" on the next
        // update() call after the flag resets.
        previousState = currentState;
        return;
      }

      // Plugin State → Y.js sync (only if changed AND not initial defaults)
      if (previousState && currentState) {
        if (!isEqual(previousState.selectOptions, currentState.selectOptions)) {
          yMap.set("selectOptions", currentState.selectOptions);
        }
        if (
          !isEqual(previousState.runConfiguration, currentState.runConfiguration)
        ) {
          yMap.set("runConfiguration", currentState.runConfiguration);
        }
      }
      // NOTE: Removed the initial "seed Y.Map with defaults" branch.
      // Writing schema defaults (e.g. empty labware) to Y.Map creates a
      // CRDT conflict with the sandbox-service's writes — whichever Y.Doc
      // has the higher random clientID wins, silently discarding the other's
      // labware. ProseMirror already has schema defaults; the Y.Map only
      // needs values when a real change occurs.

      previousState = currentState;
    },

    destroy: () => {
      yMap.unobserve(yMapObserver);
    },
  };
}

// ============================================================================
// TIPTAP EXTENSION
// ============================================================================

export const DocumentAttributesExtension =
  Extension.create<DocumentAttributesPluginOptions>({
    name: "documentAttributesPlugin",

    addOptions() {
      return {
        document: null,
        field: "documentAttributes",
      };
    },

    addProseMirrorPlugins() {
      const yDoc = this.options.document;
      const field = this.options.field ?? "documentAttributes";

      return [
        new Plugin({
          key: docAttrPluginKey,

          state: {
            init: (_, state) => {
              return buildInitialState(state, yDoc, field);
            },

            apply: (
              tr: Transaction,
              currentState: DocumentAttributesState
            ): DocumentAttributesState => {
              const action = tr.getMeta(docAttrPluginKey) as
                | DocumentAttributesAction
                | undefined;

              if (!action) {
                return currentState;
              }

              return applyAction(currentState, action);
            },
          },

          view: (editorView) => {
            return createYjsSyncView(editorView, yDoc, field);
          },
        }),
      ];
    },
  });

export default DocumentAttributesExtension;
